/**
 * Extract suggestions for remitentes, clientes, and agentes from extracted/editable data
 * Suggestions based on unique combinations: nombre + eori (remitentes/agentes) or nombre + nif (clientes) across all declarations
 */

/**
 * Create a unique key for a remitente/agente based on nombre and eori
 */
const createRemitenteAgenteKey = (item) => {
  const nombre = (item?.nombre || '').trim().toLowerCase();
  const eori = (item?.eori || '').trim().toLowerCase();
  return `${nombre}|${eori}`;
};

/**
 * Create a unique key for a cliente based on nombre and nif
 */
const createClienteKey = (item) => {
  const nombre = (item?.nombre || '').trim().toLowerCase();
  const nif = (item?.nif || '').trim().toLowerCase();
  return `${nombre}|${nif}`;
};

/**
 * Extract suggestions from data array
 * @param {Array} dataArray - Array of declarations (extractedData or editableData)
 * @param {number} currentDeclarationIndex - Index of current declaration (to exclude from suggestions)
 * @returns {Object} Object with remitentes, clientes, and agentes suggestions
 */
export const extractSuggestionsFromData = (dataArray, currentDeclarationIndex = -1) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return {
      remitentes: [],
      clientes: [],
      agentes: []
    };
  }

  // Maps to store unique items with their occurrence count
  const remitentesMap = new Map();
  const clientesMap = new Map();
  const agentesMap = new Map();

  // Iterate through all declarations
  dataArray.forEach((declaration, index) => {
    // Skip current declaration to avoid suggesting what's already there
    if (index === currentDeclarationIndex) {
      return;
    }

    // Extract remitentes
    if (Array.isArray(declaration.remitentes) && declaration.remitentes.length > 0) {
      declaration.remitentes.forEach((remitente) => {
        if (remitente && typeof remitente === 'object' && remitente.nombre) {
          const key = createRemitenteAgenteKey(remitente);
          if (!remitentesMap.has(key)) {
            remitentesMap.set(key, {
              ...remitente,
              count: 0,
              occurrences: []
            });
          }
          const entry = remitentesMap.get(key);
          entry.count += 1;
          entry.occurrences.push(index);
        }
      });
    }

    // Extract clientes
    if (Array.isArray(declaration.clientes) && declaration.clientes.length > 0) {
      declaration.clientes.forEach((cliente) => {
        if (cliente && typeof cliente === 'object' && cliente.nombre) {
          const key = createClienteKey(cliente);
          if (!clientesMap.has(key)) {
            clientesMap.set(key, {
              ...cliente,
              count: 0,
              occurrences: []
            });
          }
          const entry = clientesMap.get(key);
          entry.count += 1;
          entry.occurrences.push(index);
        }
      });
    }

    // Extract agentes
    if (Array.isArray(declaration.agentes) && declaration.agentes.length > 0) {
      declaration.agentes.forEach((agente) => {
        if (agente && typeof agente === 'object' && agente.nombre) {
          const key = createRemitenteAgenteKey(agente);
          if (!agentesMap.has(key)) {
            agentesMap.set(key, {
              ...agente,
              count: 0,
              occurrences: []
            });
          }
          const entry = agentesMap.get(key);
          entry.count += 1;
          entry.occurrences.push(index);
        }
      });
    }
  });

  // Convert maps to arrays with scores (score based on frequency: more occurrences = higher score)
  const totalDeclarations = dataArray.length;
  
  const remitentes = Array.from(remitentesMap.values())
    .map(item => ({
      nombre: item.nombre,
      eori: item.eori || '',
      nif: item.nif || '',
      // Score: frequency / total declarations (normalized to 0-1)
      score: Math.min(item.count / totalDeclarations, 1),
      reason: `Found in ${item.count} declaration${item.count > 1 ? 's' : ''}`,
      count: item.count
    }))
    .sort((a, b) => b.score - a.score); // Sort by score descending

  const clientes = Array.from(clientesMap.values())
    .map(item => ({
      nombre: item.nombre,
      nif: item.nif || '',
      eori: item.eori || '',
      // Score: frequency / total declarations (normalized to 0-1)
      score: Math.min(item.count / totalDeclarations, 1),
      reason: `Found in ${item.count} declaration${item.count > 1 ? 's' : ''}`,
      count: item.count
    }))
    .sort((a, b) => b.score - a.score); // Sort by score descending

  const agentes = Array.from(agentesMap.values())
    .map(item => ({
      nombre: item.nombre,
      eori: item.eori || '',
      nif: item.nif || '',
      // Score: frequency / total declarations (normalized to 0-1)
      score: Math.min(item.count / totalDeclarations, 1),
      reason: `Found in ${item.count} declaration${item.count > 1 ? 's' : ''}`,
      count: item.count
    }))
    .sort((a, b) => b.score - a.score); // Sort by score descending

  return {
    remitentes,
    clientes,
    agentes
  };
};
